import _ from 'lodash';
import { ComponentProps, type ReactNode, RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { ConversationIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Empty, Skeleton, tokens } from '@treams/treams-ui';

import Comment from './components/Comment';
import CommentComposer, { type CommentComposerHandle } from './components/CommentComposer';

const types = {
	EvaluationSubmission: graphql.CommentTargetType.EvaluationSubmission,
	FeedbackResponse: graphql.CommentTargetType.FeedbackResponse,
	Goal: graphql.CommentTargetType.Goal,
	Note: graphql.CommentTargetType.Note,
	Compliment: graphql.CommentTargetType.Compliment,
};

function CommentsSkeleton() {
	return (
		<Skeleton.Repeat count={3}>
			<Box mb={2}>
				<Comment.Skeleton />
			</Box>
		</Skeleton.Repeat>
	);
}

type CommentViewWithMetadata = ComponentProps<typeof Comment>['comment'] &
	Pick<graphql.Comment, 'metadata'>;

export function CommentsWithData(
	props: CommentsProps & {
		comments: CommentViewWithMetadata[];
		onRefetch: () => void;
		hideEmptyState?: boolean;
	},
) {
	const { t } = useTranslation();
	const [comments, setComments] = useState(props.comments);

	// Refresh once a comment is created.
	const commentIds = props.comments.map(({ id }) => id).join(',');
	useEffect(() => setComments(props.comments), [commentIds, props.comments]);

	// Refresh once a comment is archived.
	const isArchived = props.comments.filter(({ isArchived }) => isArchived);
	useEffect(() => setComments(props.comments), [isArchived, props.comments]);

	// Filter comments by metadata in the case of conversations
	// to make sure we show the right comments per question or form item.
	const filteredComments = comments.filter((comment) =>
		_.isEqual(
			_.fromPairs(comment.metadata?.map(({ key, value }) => [key, value])),
			props.metadata ?? {},
		),
	);

	// Sort comments by creation date to make sure the newest
	// comments are at the top.
	const sortedComments = filteredComments.sort((a, b) => {
		if (props.alignComposer === 'top') {
			return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
		}

		return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
	});

	const composerElement = (
		<CommentComposer
			ref={props.composerRef}
			targetID={props.target.id}
			targetType={types[props.target.__typename!]}
			metadata={props.metadata}
			autoFocus={props.autoFocus}
			secondaryAction={props.composerSecondaryAction}
			onCancel={props.onCancelComposer}
			onCreate={(comment: CommentViewWithMetadata) => {
				setComments([comment].concat(comments ?? []));
				props.onCancelComposer?.();
			}}
			onRefresh={props.onRefetch}
		/>
	);

	return (
		<>
			{props.target.viewerCanComment &&
				props.composer !== false &&
				props.alignComposer === 'top' && <Box mb={4}>{composerElement}</Box>}

			{filteredComments.length === 0 && !props.hideEmptyState ? (
				props.empty === false ? null : (
					<Flex justifyContent="center" py={`${tokens.padding.control}px`}>
						<Empty icon={<ConversationIcon />} title={t('comments.empty.title')} />
					</Flex>
				)
			) : (
				sortedComments.map((comment) => (
					<Box key={comment.id} mb={3}>
						<Comment comment={comment} onRefresh={props.onRefetch} />
					</Box>
				))
			)}

			{props.target.viewerCanComment &&
				props.composer !== false &&
				(props.alignComposer === undefined || props.alignComposer === 'bottom') && (
					<Box mt={4}>{composerElement}</Box>
				)}
		</>
	);
}

type CommentTargetView = Pick<graphql.CommentTarget, 'viewerCanComment'> & {
	__typename?: keyof typeof types;
	id: string;
};

type CommentsProps = {
	target: CommentTargetView;
	metadata?: Record<string, string>;
	empty?: boolean;
	composer?: boolean;
	alignComposer?: 'top' | 'bottom';
	composerRef?: RefObject<CommentComposerHandle>;
	composerSecondaryAction?: ReactNode;
	autoFocus?: boolean;
	hideEmptyState?: boolean;
	onCancelComposer?: () => void;
	onCommentsLoaded?: (comments: CommentViewWithMetadata[]) => void;
};

export default function Comments(props: CommentsProps) {
	const target = {
		targetType: types[props.target.__typename!],
		targetID: props.target.id,
	};

	const query = graphql.useCommentsQuery({
		variables: { ...target },
	});

	useEffect(() => {
		if (query.data) {
			props.onCommentsLoaded?.(query.data.comments);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query.data]);

	return (
		<Box>
			{query.data ? (
				<CommentsWithData
					{...props}
					hideEmptyState={props.hideEmptyState}
					comments={query.data.comments}
					onRefetch={() => query.refetch()}
				/>
			) : (
				<CommentsSkeleton />
			)}
		</Box>
	);
}

Comments.Skeleton = CommentsSkeleton;
